import { put, call, takeLatest, ForkEffect } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import * as _ from 'lodash';
import toast from 'react-hot-toast';
import {
  authActions,
  ICheckUsernameOrEmailTakenPayload,
  ISignUpPayload,
  IValidateOtpPayload,
  LogInPayload,
} from './authSlice';
import { logIn, logOut } from '../../api/authApi';
import {
  forgotPassword,
  resendEmailOtp,
  resendNewEmailOtp,
  signUp,
  updatePassword,
  validateNewEmail,
  validateOtp,
  validatePasswordResetToken,
  validateUserData,
} from '../../api/userApi';
import { persistor, store } from '../store';
import { HTTPErrorTypes } from '../../utils/enums/http-error-types.enum';
import { OtpVerifyStatus } from '../../utils/enums/otp-verify-status.enum';
import { UserStatus } from '../../utils/enums/user-status.enum';
import { subscriptionActions } from '../subscription/subscriptionSlice';
import { toastStyles } from '../../utils/styles';

function* watchLogIn({ payload }: { payload: LogInPayload }) {
  const { email, password } = payload;
  try {
    yield put(authActions.setComponentStatus({ isSubmitting: true }));

    const res: AxiosResponse = yield call(logIn, email, password);

    if (res.status === 200) {
      const { AccessToken, RefreshToken, accountStatus, userRole, organizationId, stripeAccountId } = res.data;
      yield put(authActions.setAccountStatus(accountStatus));
      yield put(authActions.setUserRole(userRole));
      yield put(authActions.setOrganizationId(organizationId));
      yield put(authActions.setLogInStatus(true));
      yield put(authActions.setStripeAccountId(stripeAccountId));
      yield put(authActions.setTokens({ accessToken: AccessToken, refreshToken: RefreshToken }));

      if (accountStatus === 'locked') {
        yield put(authActions.setComponentStatus({ isSubmitting: false }));
      } else if (accountStatus === 'inactive') {
        yield put(authActions.handleInactiveAccount());
        yield put(authActions.setAccountStatus(accountStatus));
      } else {
        yield put(authActions.setComponentStatus({ isPageLoading: true }));
        yield put(authActions.clearComponentStatus());
      }
    }
  } catch (err: any) {
    if (err.response && err.response.status === 307) {
      yield put(
        authActions.setComponentStatus({
          userStatus: UserStatus.PENDING,
          countdownStartTime: Date.now().toString(),
        }),
      );
      const { baseValue, plan, email: businessEmail } = err.response.data;
      yield put(subscriptionActions.setProductDetails({ baseValue, name: plan }));
      yield put(authActions.setSignUpData({ lastActiveStep: 0, businessEmail }));
    } else if (_.get(err, 'response.data.data.error') === HTTPErrorTypes.ACCOUNT_DEACTIVATED) {
      yield put(
        authActions.setComponentStatus({
          error: 'Your account has been locked by the Admin. Please contact the Porolis Administration',
        }),
      );
    } else if (_.get(err, 'response.data.data.error') === HTTPErrorTypes.INVALID_GRANT) {
      yield put(authActions.setComponentStatus({ error: 'Invalid Email Address/Username or Password!' }));
    } else if (_.get(err, 'response.data.data.error') === HTTPErrorTypes.ACCOUNT_NOT_FOUND) {
      yield put(authActions.setComponentStatus({ error: 'Couldn’t find your account!' }));
    } else {
      yield put(
        authActions.setComponentStatus({
          error: _.get(err, 'response.data.data.error_description', 'Something went wrong'),
        }),
      );
    }

    yield put(authActions.setLogInStatus(false));
  }
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchLogOut() {
  try {
    yield call(logOut);
    yield put(authActions.setLogInStatus(false));
    yield put(authActions.clearComponentStatus());
    yield put(authActions.clearPreviousPath());

    store.dispatch({ type: 'CLEAR_STORE' });
    persistor.purge().then(() => {
      persistor.persist();
    });
  } catch (err: any) {
    yield put(authActions.setComponentStatus({ error: err.response.data.message, isLogoutProcessing: false }));
    toast.error('Error Logging Out, Please Try Again', toastStyles.error);
  }
}

function* watchSendResetPasswordLink({ payload }: { payload: any }) {
  const { email } = payload;
  try {
    yield call(forgotPassword, email);
    yield put(authActions.setComponentStatus({ isEmailSent: true }));
  } catch (err: any) {
    yield put(authActions.setComponentStatus({ error: err.response.data.message }));
  }
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchValidatePasswordResetToken({ payload }: { payload: any }) {
  const { token } = payload;
  try {
    yield call(validatePasswordResetToken, token);
  } catch (err: any) {
    if (err.response.status === 406) {
      yield put(authActions.setComponentStatus({ isInvalidToken: true }));
    }
  }
  yield put(authActions.setComponentStatus({ isLoading: false }));
}

function* watchUpdatePassword({ payload }: { payload: any }) {
  const { token, newPassword, isNewUser } = payload;
  try {
    const res: AxiosResponse = yield call(updatePassword, token, newPassword, isNewUser);
    yield put(authActions.setComponentStatus({ role: res.data.role }));
  } catch (err: any) {
    if (err.response.status === 406) {
      yield put(authActions.setComponentStatus({ isInvalidToken: true }));
    }
    yield put(authActions.setComponentStatus({ error: err.response.data.message }));
  }
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchSignUp({ payload }: { payload: ISignUpPayload }) {
  try {
    yield put(authActions.setComponentStatus({ isSubmitting: true }));
    yield call(signUp, payload);
    yield put(
      authActions.setComponentStatus({
        isSignUpSuccess: true,
        isSubmitting: false,
        countdownStartTime: Date.now().toString(),
      }),
    );
  } catch (error: any) {
    yield put(
      authActions.setComponentStatus({
        isSignUpSuccess: false,
        error: error.response.data.message,
        isSubmitting: false,
      }),
    );
  }
}

function* watchValidateOtp({ payload }: { payload: IValidateOtpPayload }) {
  try {
    yield put(
      authActions.setComponentStatus({ isSubmitting: true, error: '', verifyStatus: OtpVerifyStatus.VERIFYING }),
    );
    const res: AxiosResponse = yield call(validateOtp, payload);
    if (res.status === 200) {
      const { stripeAccountId } = res.data;
      yield put(authActions.setStripeAccountId(stripeAccountId));
      yield put(
        authActions.setComponentStatus({
          isSubmitting: false,
          countdownStartTime: Date.now().toString(),
          verifyStatus: OtpVerifyStatus.VERIFIED,
        }),
      );
    }
  } catch (err: any) {
    yield put(
      authActions.setComponentStatus({
        error: err.response.data.message,
        verifyStatus: OtpVerifyStatus.FAILED,
        isSubmitting: false,
      }),
    );
  }
}

function* watchValidateNewEmailOtp({ payload }: { payload: IValidateOtpPayload }) {
  try {
    yield put(
      authActions.setComponentStatus({
        isSubmitting: true,
        error: '',
        newEmailverifyStatus: OtpVerifyStatus.VERIFYING,
      }),
    );
    const res: AxiosResponse = yield call(validateNewEmail, payload);
    if (res.status === 200) {
      yield put(
        authActions.setComponentStatus({
          isSubmitting: false,
          countdownStartTime: Date.now().toString(),
          newEmailverifyStatus: OtpVerifyStatus.VERIFIED,
        }),
      );
    }
    toast.success('Profile Updated', toastStyles.success);
  } catch (err: any) {
    yield put(
      authActions.setComponentStatus({
        error: err.response.data.message,
        newEmailverifyStatus: OtpVerifyStatus.FAILED,
        isSubmitting: false,
      }),
    );
  }
}

function* watchResendEmailOtp({ payload }: { payload: { email: string } }) {
  try {
    yield put(authActions.setComponentStatus({ isSubmitting: true }));
    yield call(resendEmailOtp, payload.email);
    yield put(authActions.setComponentStatus({ countdownStartTime: Date.now().toString() }));
  } catch (err) {
    if (_.get(err, 'response.status') === 401) {
      yield put(authActions.setComponentStatus({ error: 'Email or Password Incorrect!' }));
    } else {
      yield put(
        authActions.setComponentStatus({
          error: _.get(err, 'response.data.error_description', 'Something went wrong'),
        }),
      );
    }
  }
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchResendNewEmailOtp({ payload }: { payload: { email: string } }) {
  try {
    yield put(authActions.setComponentStatus({ isSubmitting: true }));
    yield call(resendNewEmailOtp, payload.email);
    yield put(authActions.setComponentStatus({ countdownStartTime: Date.now().toString() }));
  } catch (err) {
    if (_.get(err, 'response.status') === 401) {
      yield put(authActions.setComponentStatus({ error: 'Email or Password Incorrect!' }));
    } else {
      yield put(
        authActions.setComponentStatus({
          error: _.get(err, 'response.data.error_description', 'Something went wrong'),
        }),
      );
    }
  }
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchCheckUsernameOrEmailTakenAsync({ payload }: { payload: ICheckUsernameOrEmailTakenPayload }) {
  const { input, isUsername } = payload;
  try {
    if (isUsername) {
      yield put(authActions.resetIsUserNameAlreadyExistsStatus());
      const res: AxiosResponse = yield call(validateUserData, { username: input });
      yield put(authActions.setComponentStatus({ isUsernameAlreadyExists: res.data }));
    } else {
      yield put(authActions.resetIsEmailAlreadyExistsStatus());
      const res: AxiosResponse = yield call(validateUserData, { email: input });
      yield put(authActions.setComponentStatus({ isEmailAlreadyExists: res.data }));
    }
  } catch (err: any) {
    yield put(authActions.setComponentStatus({ error: err.response.data.message }));
  }
}

export function* watchAuthSagas(): Generator<ForkEffect, void> {
  yield takeLatest(authActions.logInAsync, watchLogIn);
  yield takeLatest(authActions.logOutAsync, watchLogOut);
  yield takeLatest(authActions.sendPasswordResetLinkAsync, watchSendResetPasswordLink);
  yield takeLatest(authActions.validatePasswordResetTokenAsync, watchValidatePasswordResetToken);
  yield takeLatest(authActions.updatePasswordAsync, watchUpdatePassword);
  yield takeLatest(authActions.signUpAsync, watchSignUp);
  yield takeLatest(authActions.validateOtpAsync, watchValidateOtp);
  yield takeLatest(authActions.validateNewEmailOtpAsync, watchValidateNewEmailOtp);
  yield takeLatest(authActions.resendEmailOtpAsync, watchResendEmailOtp);
  yield takeLatest(authActions.resendNewEmailOtpAsync, watchResendNewEmailOtp);
  yield takeLatest(authActions.checkUsernameOrEmailTakenAsync, watchCheckUsernameOrEmailTakenAsync);
}

const authSagas = watchAuthSagas;
export default authSagas;
